/**
 * SIELE 考试特定的题目类型定义
 */

import {
  QuestionType,
  createExamQuestionTypeDefinition,
  createFieldDefinition,
  type ExamSpecificQuestionTypeDefinition,
} from '@examflow/core';

// SIELE 等级范围（基于 CEFR，最高到 C1）
const SIELE_VALID_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

/**
 * 创建 SIELE 等级字段定义（各 Section 共用）
 */
function createSieleLevelField() {
  return createFieldDefinition('sieleLevel', 'string', true, 'SIELE 等级 (A1-C1)', {
    validator: (value) => {
      return SIELE_VALID_LEVELS.includes(value as string)
        ? true
        : `不支持的 SIELE 等级: ${value}，必须是以下之一: ${SIELE_VALID_LEVELS.join(', ')}`;
    },
  });
}

/**
 * 创建任务编号字段定义（各 Section 共用）
 */
function createTaskNumberField() {
  return createFieldDefinition('taskNumber', 'number', true, '任务编号', {
    validator: (value) => {
      const num = Number(value);
      if (Number.isNaN(num) || num < 1) {
        return 'SIELE 任务编号必须是大于等于 1 的数字';
      }
      return true;
    },
  });
}

/**
 * SIELE 阅读理解题定义
 */
export const sieleReadingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'siele_reading',
    QuestionType.SINGLE_CHOICE,
    'SIELE 阅读理解题',
    {
      requiredStimulus: ['passage'],
      extraFields: {
        sieleLevel: createSieleLevelField(),
        // SIELE Global 阅读部分共 5 个任务
        taskNumber: createTaskNumberField(),
      },

      customValidator: (question) => {
        const errors = [];

        // SIELE Global 阅读部分为 5 个任务
        const taskNumber = Number(question.taskNumber);
        if (!Number.isNaN(taskNumber) && (taskNumber < 1 || taskNumber > 5)) {
          errors.push({
            field: 'taskNumber',
            message: 'SIELE 阅读理解任务编号必须在 1-5 之间',
            code: 'SIELE_READING_TASK_NUMBER',
          });
        }

        return errors;
      },
    }
  );

/**
 * SIELE 听力理解题定义
 */
export const sieleListeningDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'siele_listening',
    QuestionType.SINGLE_CHOICE,
    'SIELE 听力理解题',
    {
      requiredStimulus: ['audio'],
      extraFields: {
        sieleLevel: createSieleLevelField(),
        // SIELE Global 听力部分共 5 个任务
        taskNumber: createTaskNumberField(),
        // 音频时长（秒）
        audioDuration: createFieldDefinition(
          'audioDuration',
          'number',
          false,
          '音频时长（秒）'
        ),
      },

      customValidator: (question) => {
        const errors = [];

        // SIELE Global 听力部分为 5 个任务
        const taskNumber = Number(question.taskNumber);
        if (!Number.isNaN(taskNumber) && (taskNumber < 1 || taskNumber > 5)) {
          errors.push({
            field: 'taskNumber',
            message: 'SIELE 听力理解任务编号必须在 1-5 之间',
            code: 'SIELE_LISTENING_TASK_NUMBER',
          });
        }

        // 音频时长验证
        if (question.audioDuration != null) {
          const audioDuration = Number(question.audioDuration);
          if (!Number.isNaN(audioDuration) && audioDuration <= 0) {
            errors.push({
              field: 'audioDuration',
              message: 'SIELE 听力音频时长必须为正数',
              code: 'SIELE_AUDIO_DURATION_INVALID',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * SIELE 书面表达题定义
 */
export const sieleWritingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'siele_writing',
    QuestionType.FILL_BLANK, // 写作为开放式作答，用填空类型承载
    'SIELE 书面表达题',
    {
      responseMode: 'open_ended',
      extraFields: {
        sieleLevel: createSieleLevelField(),
        // SIELE Global 书面表达部分共 2 个任务
        taskNumber: createTaskNumberField(),
        // 最低字数要求
        minWordCount: createFieldDefinition(
          'minWordCount',
          'number',
          false,
          '最低字数要求'
        ),
      },

      customValidator: (question) => {
        const errors = [];

        // SIELE Global 书面表达部分为 2 个任务
        const taskNumber = Number(question.taskNumber);
        if (!Number.isNaN(taskNumber) && (taskNumber < 1 || taskNumber > 2)) {
          errors.push({
            field: 'taskNumber',
            message: 'SIELE 书面表达任务编号必须在 1-2 之间',
            code: 'SIELE_WRITING_TASK_NUMBER',
          });
        }

        // 最低字数验证
        if (question.minWordCount != null) {
          const minWordCount = Number(question.minWordCount);
          if (!Number.isNaN(minWordCount) && minWordCount <= 0) {
            errors.push({
              field: 'minWordCount',
              message: 'SIELE 写作最低字数要求必须为正数',
              code: 'SIELE_WRITING_MIN_WORDS',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * SIELE 口语表达题定义
 */
export const sieleSpeakingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'siele_speaking',
    QuestionType.FILL_BLANK, // 口语为开放式作答，用填空类型承载
    'SIELE 口语表达题',
    {
      responseMode: 'open_ended',
      extraFields: {
        sieleLevel: createSieleLevelField(),
        // SIELE Global 口语表达部分共 5 个任务
        taskNumber: createTaskNumberField(),
      },

      customValidator: (question) => {
        const errors = [];

        // SIELE Global 口语表达部分为 5 个任务
        const taskNumber = Number(question.taskNumber);
        if (!Number.isNaN(taskNumber) && (taskNumber < 1 || taskNumber > 5)) {
          errors.push({
            field: 'taskNumber',
            message: 'SIELE 口语表达任务编号必须在 1-5 之间',
            code: 'SIELE_SPEAKING_TASK_NUMBER',
          });
        }

        return errors;
      },
    }
  );

/**
 * 所有 SIELE 题目类型定义
 */
export const SIELE_QUESTION_TYPE_DEFINITIONS: Record<
  string,
  ExamSpecificQuestionTypeDefinition
> = {
  [sieleReadingDefinition.id]: sieleReadingDefinition,
  [sieleListeningDefinition.id]: sieleListeningDefinition,
  [sieleWritingDefinition.id]: sieleWritingDefinition,
  [sieleSpeakingDefinition.id]: sieleSpeakingDefinition,
};

/**
 * 根据基础类型获取 SIELE 题目类型定义
 */
export function getSIELEQuestionTypeDefinition(
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition | undefined {
  return Object.values(SIELE_QUESTION_TYPE_DEFINITIONS).find(
    (def) => def.baseType === baseType
  );
}

/**
 * 获取 SIELE 题目类型的 Section 建议
 * 根据题目类型推荐应该属于哪个 Section
 */
export function getSIELESectionSuggestion(
  questionTypeId: string
): 'reading' | 'listening' | 'writing' | 'speaking' | null {
  const map: Record<string, 'reading' | 'listening' | 'writing' | 'speaking'> = {
    [sieleReadingDefinition.id]: 'reading',
    [sieleListeningDefinition.id]: 'listening',
    [sieleWritingDefinition.id]: 'writing',
    [sieleSpeakingDefinition.id]: 'speaking',
  };
  return map[questionTypeId] ?? null;
}
